import {
  Area,
  AreaChart,
  CartesianGrid,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { AssetTelemetryRow } from '../dto/asset.js';
import type { ClientTelemetryDay } from '../dto/client-asset.js';
import { SX_PALETTE } from './charts.js';

// Theme-token colors with fallbacks — see FmvLine.tsx.
const BORDER = 'hsl(var(--border, 240 5% 91%))';
const MUTED = 'hsl(var(--muted-foreground, 215 20% 65%))';

/**
 * GPU-temp area sparkline (avg filled, max overlay line) for the asset
 * detail telemetry view in both portals. Data is oldest→newest.
 */
export function TelemetrySparkline({
  data,
}: {
  data: (AssetTelemetryRow | ClientTelemetryDay)[];
}) {
  if (data.length === 0) {
    return (
      <div className="flex h-32 items-center justify-center text-xs text-muted-foreground">
        No telemetry collected in the last 30 days.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={160}>
      <AreaChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: -8 }}>
        <defs>
          <linearGradient id="avgTempFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={SX_PALETTE[0]} stopOpacity={0.25} />
            <stop offset="100%" stopColor={SX_PALETTE[0]} stopOpacity={0.02} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
        <XAxis dataKey="day" stroke={MUTED} fontSize={10} tickLine={false} />
        <YAxis stroke={MUTED} fontSize={10} tickLine={false} width={32} unit="°" />
        <Tooltip
          contentStyle={{
            fontSize: 12,
            borderRadius: 6,
            borderColor: BORDER,
            backgroundColor: 'hsl(var(--popover, 0 0% 100%))',
            color: 'hsl(var(--popover-foreground, 240 10% 4%))',
          }}
          formatter={(value: unknown, key: unknown) =>
            key === 'anyXidNonzero' ? String(value) : `${(value as number)?.toFixed?.(1) ?? value}°C`
          }
        />
        <Area
          type="monotone"
          dataKey="avgGpuTempC"
          stroke={SX_PALETTE[0]}
          strokeWidth={2}
          fill="url(#avgTempFill)"
          name="Avg GPU temp"
        />
        <Line
          type="monotone"
          dataKey="maxGpuTempC"
          stroke={SX_PALETTE[5]}
          strokeWidth={1.5}
          dot={false}
          name="Max GPU temp"
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
